import { useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { tokens } from "@core/theme/tokens";
import { Icon } from "@core/icons";

// Registro de adaptaciones de acceso: BigButton corresponde a Big (:67),
// PasswordField a Campo (:87) con visibilidad y Notice a Aviso (:103) del
// prototipo. No son primitivas del portal administrativo y no se reestilizan.
// Primitivas propias de la pantalla de acceso, adaptadas de
// context/prototype/source/validador.jsx (Campo :87, Aviso :103, Big :67) a
// escritorio y a tokens de STD-003 — no al objeto `V` del prototipo.

export function Field({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  error,
  right,
  autoComplete,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: boolean;
  right?: ReactNode;
  autoComplete?: string;
}) {
  return (
    <label style={{ display: "block", marginBottom: 16 }}>
      <div
        style={{
          font: `600 10.5px/1.3 ${tokens.font.ui}`,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: tokens.text.tertiary,
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div style={{ position: "relative" }}>
        <input
          type={type}
          value={value}
          autoComplete={autoComplete}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          placeholder={placeholder}
          style={{
            width: "100%",
            boxSizing: "border-box",
            minHeight: 44,
            padding: right ? "11px 76px 11px 13px" : "11px 13px",
            borderRadius: 11,
            background: "rgba(255,255,255,0.03)",
            border: `1px solid ${error ? tokens.danger.line : tokens.surface.line2}`,
            color: tokens.text.primary,
            font: `400 13.5px ${tokens.font.ui}`,
            outline: "none",
          }}
        />
        {right}
      </div>
    </label>
  );
}

// Mismo campo que `Field`, con el botón "Ver"/"Ocultar" ya resuelto — extraído
// para no repetir el bloque en LoginScreen/ChangePasswordScreen/NewPasswordScreen
// (las tres pantallas de contraseña deben comportarse igual).
export function PasswordField({
  label,
  value,
  onChange,
  placeholder,
  error,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: boolean;
  autoComplete?: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <Field
      label={label}
      type={visible ? "text" : "password"}
      value={value}
      onChange={onChange}
      {...(placeholder !== undefined ? { placeholder } : {})}
      {...(autoComplete !== undefined ? { autoComplete } : {})}
      {...(error !== undefined ? { error } : {})}
      right={
        <button
          type="button"
          onClick={() => {
            setVisible((v) => !v);
          }}
          style={{
            position: "absolute",
            right: 4,
            top: 4,
            bottom: 4,
            padding: "0 12px",
            borderRadius: 9,
            background: "transparent",
            border: "none",
            color: tokens.text.tertiary,
            font: `600 12px ${tokens.font.ui}`,
            cursor: "pointer",
          }}
        >
          {visible ? "Ocultar" : "Ver"}
        </button>
      }
    />
  );
}

export function Notice({
  tone = "danger",
  children,
}: {
  tone?: "danger" | "info";
  children: ReactNode;
}) {
  const skin =
    tone === "danger"
      ? {
          bg: "rgba(214,124,104,0.1)",
          line: "rgba(214,124,104,0.34)",
          col: "#DFA394",
        }
      : { bg: tokens.gold.dim, line: tokens.gold.line, col: tokens.gold.light };
  return (
    <div
      role="alert"
      style={{
        display: "flex",
        gap: 11,
        padding: "11px 14px",
        borderRadius: 11,
        background: skin.bg,
        border: `1px solid ${skin.line}`,
        marginBottom: 18,
      }}
    >
      <Icon
        name={tone === "danger" ? "alert" : "check"}
        size={19}
        color={skin.col}
        stroke={1.8}
        style={{ flex: "none", marginTop: 1 }}
      />
      <div
        style={{
          font: `500 13px/1.55 ${tokens.font.ui}`,
          color: skin.col,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function BigButton({
  children,
  onClick,
  type = "button",
  tone = "primary",
  disabled,
  style,
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: "button" | "submit";
  tone?: "primary" | "ghost";
  disabled?: boolean;
  style?: CSSProperties;
}) {
  const skins: Record<string, CSSProperties> = {
    primary: {
      background: tokens.silver.grad,
      color: tokens.surface.bg,
      border: `1px solid ${tokens.surface.line2}`,
    },
    ghost: {
      background: "transparent",
      color: tokens.text.primary,
      border: `1px solid ${tokens.surface.line2}`,
    },
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        minHeight: 50,
        borderRadius: 999,
        cursor: disabled ? "not-allowed" : "pointer",
        font: `600 14px/1 ${tokens.font.ui}`,
        opacity: disabled ? 0.5 : 1,
        ...skins[tone],
        ...style,
      }}
    >
      {children}
    </button>
  );
}

export function LinkButton({
  children,
  onClick,
  style,
}: {
  children: ReactNode;
  onClick: () => void;
  style?: CSSProperties;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: "none",
        border: "none",
        color: tokens.text.secondary,
        font: `500 13px ${tokens.font.ui}`,
        letterSpacing: "0.01em",
        padding: 8,
        cursor: "pointer",
        ...style,
      }}
    >
      {children}
    </button>
  );
}
